import React, { useEffect, useContext, useRef, use } from 'react';
import { AppContext } from '../../../context/Context';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, Skeleton } from '@mui/material';
import { SocketContext } from '../../../context/SocketContext';
import toast from 'react-hot-toast';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material/';

function ModalBlockedToBuyDataBingo() {
  const { t } = useTranslation();
  const { socket } = useContext(SocketContext);

  const {
    updateUserInfoBlocked,
    updateShowModalBlockedToBuyDataBingo,
    updateFocusOnMainScreen,
    userInfoBlocked,
    lastDataBingoTicket,
    updateLastDataBingoTicket,
    updateIsLoadingScreen,
    operator,
    session,
    hall,
    userInfo,
    WGBingoID,
    updateShowModalSellKiosk,
    updateSellType,
    updateVendorInfo,
    updateInfoAutoOpenAfterCloseZeroTicket,
    infoAutoOpenAfterCloseZeroTicket,
    OKBingoID,
  } = useContext(AppContext);

  const infoAutoOpenAfterCloseZeroTicketRef = useRef(
    infoAutoOpenAfterCloseZeroTicket
  );

  const [balanceOK, setBalanceOK] = React.useState(null);
  const [isOKInUse, setIsOKInUse] = React.useState(false);
  const [isWGInUse, setIsWGInUse] = React.useState(false);

  useEffect(() => {
    console.log('Emitting ticketsOK with data:', {
      operator: operator,
      bingoName: hall,
      userID: userInfo.id,
      userInfo: userInfo,
      session: session,
      action: 'ticketsOK',
      OKBingoID: OKBingoID,
    });
    socket.emit('ticketsOK', {
      operator: operator,
      bingoName: hall,
      userID: userInfo.id,
      userInfo: userInfo,
      session: session,
      action: 'ticketsOK',
      OKBingoID: OKBingoID,
    });
  }, []);

  const OKBingoIDRef = useRef(OKBingoID);
  useEffect(() => {
    OKBingoIDRef.current = OKBingoID;
  }, [OKBingoID]);

  // ticketID of our pending cashout, to tell our OK reply from other halls'
  const okTicketIDRef = useRef(false);

  useEffect(() => {
    console.log(
      'infoAutoOpenAfterCloseZeroTicket updated:',
      infoAutoOpenAfterCloseZeroTicket
    );
    infoAutoOpenAfterCloseZeroTicketRef.current =
      infoAutoOpenAfterCloseZeroTicket;
  }, [infoAutoOpenAfterCloseZeroTicket]);

  const lastDataBingoTicketRef = useRef(lastDataBingoTicket);
  useEffect(() => {
    lastDataBingoTicketRef.current = lastDataBingoTicket;
    if (lastDataBingoTicket.provider === 'WG' && lastDataBingoTicket.isInUse) {
      setIsWGInUse(true);
    } else {
      setIsWGInUse(false);
    }
  }, [lastDataBingoTicket]);

  const operatorRef = useRef(operator);
  useEffect(() => {
    operatorRef.current = operator;
  }, [operator]);

  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const hallRef = useRef(hall);
  useEffect(() => {
    hallRef.current = hall;
  }, [hall]);

  const userInfoRef = useRef(userInfo);
  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  const WGBingoIDRef = useRef(WGBingoID);
  useEffect(() => {
    WGBingoIDRef.current = WGBingoID;
  }, [WGBingoID]);

  useEffect(() => {
    const handleCashoutWG = (data) => {
      if (!data) {
        toast.error(t('error.error'));
        updateIsLoadingScreen(false);

        return;
      }

      updateLastDataBingoTicket(false);
      updateShowModalBlockedToBuyDataBingo(false);
      updateIsLoadingScreen(false);

      updateVendorInfo(infoAutoOpenAfterCloseZeroTicketRef.current.vendor);
      updateSellType(infoAutoOpenAfterCloseZeroTicketRef.current.sellType);
      updateShowModalSellKiosk(true);
      updateInfoAutoOpenAfterCloseZeroTicket(false);
    };

    const handleTicketsOK = (data) => {
      if (data.error) {
        toast.error(t('error.error'));
        return;
      }
    };

    const handleBroadcastGetTicketSaldoOK = (data) => {
      if (data.error) {
        toast.error(t('error.error'));
        return;
      }

      if (
        data.ticketNumber &&
        data.ticketNumber === lastDataBingoTicketRef.current.ticketID
      ) {
        const inUse =
          (data.errorDescription &&
            (data.errorDescription.toLowerCase().includes('ticket er i bruk') ||
              data.errorDescription
                .toLowerCase()
                .includes('logget inn på maskin'))) ||
          (data.errorNumber && parseInt(data.errorNumber) === 510);

        setIsOKInUse(!!inUse);
        setBalanceOK(data.newSaldo ?? 0);

        //Test
        //setBalanceOK(0);
      }
    };

    const handleCashoutOK = (data) => {
      if (!data) {
        toast.error(t('error.error'));
        updateIsLoadingScreen(false);
        return;
      }
      okTicketIDRef.current = data.ticketID;
    };

    const handleBroadcastCashoutOK = (data) => {
      console.log('Received broadcastCashoutOK:', data);
      // Every hall gets every OK reply: skip the ones for another ticketID
      if (
        !Number.isNaN(data.ticketID) &&
        data.ticketID !== okTicketIDRef.current
      ) {
        return;
      }

      if (data.errorNumber !== '') {
        toast.error(data.errorDescription);
        updateIsLoadingScreen(false);
        return;
      }

      if (data.ticketNumber === lastDataBingoTicketRef.current.ticketID) {
        socket.emit('registerTransactionCashoutOK', {
          operator: operatorRef.current,
          bingoName: hallRef.current,
          sessionID: sessionRef.current,
          userInfo: userInfoRef.current,
          amount: 0,
          provider: 'OK D.',
          description: 'Utbetaling',
          okID: data.ticketNumber,
          action: 'registerTransactionCashoutOK',
          printer: false,
          parameter: data,
          OKBingoID: OKBingoIDRef.current,
        });
      }
    };

    const handleRegisterTransactionCashoutOK = (data) => {
      console.log('Received registerTransactionCashoutOK:', data);
      if (!data) {
        toast.error(t('error.error'));
        updateIsLoadingScreen(false);
        return;
      }

      updateLastDataBingoTicket(false);
      updateShowModalBlockedToBuyDataBingo(false);
      updateIsLoadingScreen(false);

      updateVendorInfo(infoAutoOpenAfterCloseZeroTicketRef.current.vendor);
      updateSellType(infoAutoOpenAfterCloseZeroTicketRef.current.sellType);
      updateShowModalSellKiosk(true);
      updateInfoAutoOpenAfterCloseZeroTicket(false);

      /*
      updateIsLoadingScreen(false);
      updateVendorInfo(data.vendorInfo);
      updateSellType(data.sellType);
      updateShowModalSellKiosk(true);
      */
    };

    socket.on('cashoutWG', handleCashoutWG);

    socket.on('broadcastGetTicketSaldoOK', handleBroadcastGetTicketSaldoOK);
    socket.on('ticketsOK', handleTicketsOK);
    socket.on('cashoutOK', handleCashoutOK);
    socket.on('broadcastCashoutOK', handleBroadcastCashoutOK);
    socket.on(
      'registerTransactionCashoutOK',
      handleRegisterTransactionCashoutOK
    );

    return () => {
      socket.off('cashoutWG', handleCashoutWG);

      socket.off('broadcastGetTicketSaldoOK', handleBroadcastGetTicketSaldoOK);
      socket.off('ticketsOK', handleTicketsOK);
      socket.off('cashoutOK', handleCashoutOK);
      socket.off('broadcastCashoutOK', handleBroadcastCashoutOK);
      socket.off(
        'registerTransactionCashoutOK',
        handleRegisterTransactionCashoutOK
      );
    };
  }, [socket]);

  const isZeroBalance =
    lastDataBingoTicket?.provider === 'WG'
      ? lastDataBingoTicket?.balance === 0
      : lastDataBingoTicket?.provider === 'OK D.'
        ? balanceOK === 0 && !isOKInUse
        : false;

  const handleConfirm = async () => {
    updateLastDataBingoTicket(false);
    updateShowModalBlockedToBuyDataBingo(false);
  };

  const handleCloseTicket = async () => {
    if (lastDataBingoTicketRef.current.provider === 'WG') {
      console.log('Closing WG ticket', lastDataBingoTicketRef.current.ticketID);
      updateIsLoadingScreen(true);
      socket.emit('cashoutWG', {
        operator: operatorRef.current,
        bingoName: hallRef.current,
        sessionID: sessionRef.current,
        userInfo: userInfoRef.current,
        amount: 0,
        provider: 'WG',
        description: 'Utbetaling',
        wgID: lastDataBingoTicketRef.current.ticketID,
        action: 'cashoutWG',
        printer: false,
        bingoID: WGBingoIDRef.current,
      });
    }

    if (lastDataBingoTicketRef.current.provider === 'OK D.') {
      console.log(
        'Closing OK D. ticket',
        lastDataBingoTicketRef.current.ticketID
      );
      updateIsLoadingScreen(true);
      socket.emit('cashoutOK', {
        operator: operatorRef.current,
        bingoName: hallRef.current,
        sessionID: sessionRef.current,
        userInfo: userInfoRef.current,
        amount: 0,
        provider: 'OK D.',
        description: 'Utbetaling',
        okID: lastDataBingoTicketRef.current.ticketID,
        action: 'cashoutOK',
        printer: false,
        OKBingoID: OKBingoIDRef.current,
      });
    }
  };

  return (
    <div id="react-confirm-alert">
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          background: 'rgba(3,6,18,0.88)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px 16px',
        }}
      >
        <div
          style={{
            width: 'min(760px, 96vw)',
            borderRadius: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 30px 90px rgba(0,0,0,0.65)',
            background:
              'linear-gradient(135deg, rgba(18,26,55,0.98), rgba(10,14,28,0.98))',
            padding: '40px 48px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {lastDataBingoTicket?.provider === 'OK D.' && balanceOK === null ? (
            <>
              <Skeleton
                variant="rounded"
                width="60%"
                height={34}
                sx={{ bgcolor: 'rgba(255,255,255,0.08)' }}
              />
              <Skeleton
                variant="rounded"
                width="40%"
                height={26}
                sx={{ bgcolor: 'rgba(255,255,255,0.08)' }}
              />
              <Skeleton
                variant="rounded"
                width="100%"
                height={22}
                sx={{ bgcolor: 'rgba(255,255,255,0.08)' }}
              />
              <Skeleton
                variant="rounded"
                width="80%"
                height={22}
                sx={{ bgcolor: 'rgba(255,255,255,0.08)' }}
              />
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Skeleton
                  variant="rounded"
                  width={160}
                  height={72}
                  sx={{ bgcolor: 'rgba(255,255,255,0.08)' }}
                />
                <Skeleton
                  variant="rounded"
                  width={200}
                  height={72}
                  sx={{ bgcolor: 'rgba(255,255,255,0.08)' }}
                />
              </Box>
            </>
          ) : (
            <>
              <Typography
                sx={{ color: 'white', fontSize: '28px', fontWeight: 900 }}
              >
                {t('kiosk.blockedToBuyDataBingoTitle')}
              </Typography>
              {lastDataBingoTicketRef.current.ticketID && (
                <Typography
                  sx={{ color: '#FFC727', fontSize: '22px', fontWeight: 800 }}
                >
                  {(() => {
                    const v = String(
                      lastDataBingoTicketRef.current.ticketID ?? ''
                    );
                    const len = v.length;
                    if (len <= 4) return `(CH - ${v})`;
                    if (len === 7) return `(OK D. - ${v})`;
                    return `(WG - ${v})`;
                  })()}
                </Typography>
              )}
              <Typography
                sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '24px' }}
              >
                {t('kiosk.blockedToBuyDataBingoMessage')}
              </Typography>
              {isZeroBalance && (
                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.75)',
                    fontSize: '24px',
                    mt: 1,
                  }}
                >
                  {t('kiosk.blockedToBuyDataBingoZeroBalance')}
                </Typography>
              )}
              {lastDataBingoTicket?.provider === 'OK D.' && isOKInUse && (
                <Typography sx={{ color: '#ff4d4f', fontSize: '24px', mt: 1 }}>
                  {t('kiosk.blockedToBuyDataBingoInUse')}
                </Typography>
              )}
              {lastDataBingoTicket?.provider === 'WG' && isWGInUse && (
                <Typography sx={{ color: '#ff4d4f', fontSize: '24px', mt: 1 }}>
                  {t('kiosk.blockedToBuyDataBingoInUse')}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleConfirm}
                  sx={{
                    bgcolor: '#FFC727',
                    color: '#000',
                    fontWeight: 800,
                    fontSize: '22px',
                    height: '72px',
                    px: 4,
                    boxShadow: '0 4px 18px rgba(255,199,39,0.35)',
                    '&:hover': {
                      bgcolor: '#e6b320',
                      boxShadow: '0 6px 22px rgba(255,199,39,0.5)',
                    },
                  }}
                >
                  {t('kiosk.comeBack')}
                </Button>
                {isZeroBalance && (
                  <Button
                    variant="contained"
                    startIcon={<CancelIcon />}
                    onClick={handleCloseTicket}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.08)',
                      color: 'white',
                      fontWeight: 800,
                      fontSize: '22px',
                      height: '72px',
                      px: 4,
                      border: '1px solid rgba(255,255,255,0.14)',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.14)' },
                    }}
                  >
                    {t('kiosk.blockedToBuyDataBingoCloseTicket')}
                  </Button>
                )}
              </Box>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ModalBlockedToBuyDataBingo;
